import {
  AdminSpinner,
  Box,
  Button,
  CollapsibleComponent,
  CreateCustomExpenseDto,
  CustomExpenseEntity,
  CustomExpenseOccurrenceEntity,
  deleteFinancesCustomExpense,
  deleteFinancesCustomExpenseOccurrence,
  Disclaimer,
  Div,
  EditPenIcon,
  formatAmount,
  getFinancesCustomExpenseOccurrences,
  getFinancesCustomExpenses,
  H5,
  Icon,
  InfoComponentMobile,
  lightTheme,
  ModalPortal,
  NumberType,
  P,
  patchFinancesCustomExpense,
  patchFinancesCustomExpenseOccurrence,
  postFinancesCustomExpense,
  reportError,
  SuccessToast,
  TableHeader,
  TrashIconRed,
  UpdateCustomExpenseDto,
} from '@vgrubs/components';
import { useEffect, useRef, useState } from 'react';
import { AddExpenseModal } from './AddExpenseModal';
import { EditExpenseModal } from './EditExpenseModal';
import { EditOccurrenceModal } from './EditOccurrenceModal';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// Desktop column widths — mirror the SeeLossOrdersButton table layout.
const COL = {
  name: '26%',
  amount: '15%',
  type: '12%',
  period: '21%',
  action: '26%',
} as const;

const COLUMNS: { label: string; width: string }[] = [
  { label: 'Name', width: COL.name },
  { label: 'Amount', width: COL.amount },
  { label: 'Type', width: COL.type },
  { label: 'Period', width: COL.period },
  { label: '', width: COL.action },
];

const periodLabel = (e: CustomExpenseEntity): string => {
  // One-time and recurring both anchor on startMonth/startYear.
  if (!e.startMonth || !e.startYear) return '—';
  const start = `${MONTHS[e.startMonth - 1]} ${e.startYear}`;
  if (e.type === 'oneTime') return start;
  if (!e.endMonth || !e.endYear) return `${start} – ongoing`;
  return `${start} – ${MONTHS[e.endMonth - 1]} ${e.endYear}`;
};

/** "2026-07-01" → "Jul 2026". */
const occurrenceMonthLabel = (periodDate: string): string => {
  const [year, month] = periodDate.split('-');
  const label = MONTHS[Number(month) - 1];
  return label ? `${label} ${year}` : periodDate;
};

/** One materialized month of a custom expense - no expand, just edit / delete. */
const OccurrenceRow = ({
  occurrence,
  onEdit,
  onDelete,
}: {
  occurrence: CustomExpenseOccurrenceEntity;
  onEdit: () => void;
  onDelete: () => void;
}) => (
  <Div
    $flex
    $alignCenter
    $justifyEnd
    $betweenSM
    $gap="12px"
    $p="12px 16px"
    $pSM="8px 0px"
    $borderTop
    $borderColor={lightTheme.mediumGray}
  >
    <P $m="0" $bold $fsSM="12px">
      {occurrenceMonthLabel(occurrence.periodDate)}
    </P>
    <Div $flex $alignCenter $gap="16px">
      <P $m="0" $fsSM="12px">
        {formatAmount(occurrence.amount, NumberType.USD)}
      </P>
      <Icon $pointer $size="28" onClick={onEdit}>
        <EditPenIcon
          backgroundColor={lightTheme.primary}
          iconColor={lightTheme.white}
        />
      </Icon>
      <Icon $pointer $size="28" onClick={onDelete}>
        <TrashIconRed />
      </Icon>
    </Div>
  </Div>
);

/**
 * Collapsible occurrences list. Self-manages its measured height so it can be
 * mounted in two layouts (inside the mobile card / below the desktop table row)
 * with each instance measuring the branch that's actually visible.
 */
const OccurrencesCollapsible = ({
  expanded,
  occurrences,
  onEditOccurrence,
  onDeleteOccurrence,
  marginTop,
}: {
  expanded: boolean;
  occurrences: CustomExpenseOccurrenceEntity[] | null;
  onEditOccurrence: (occurrence: CustomExpenseOccurrenceEntity) => void;
  onDeleteOccurrence: (id: number) => void;
  marginTop?: string;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const measure = () => setHeight(el.scrollHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [occurrences, expanded]);

  return (
    <CollapsibleComponent
      expanded={expanded}
      elementHeight={height}
      marginTop={marginTop}
    >
      <div ref={contentRef}>
        {occurrences && occurrences.length > 0 ? (
          occurrences.map((occ) => (
            <OccurrenceRow
              key={occ.id}
              occurrence={occ}
              onEdit={() => onEditOccurrence(occ)}
              onDelete={() => onDeleteOccurrence(occ.id)}
            />
          ))
        ) : (
          <Div $flex $center $height="37px">
            <Disclaimer $m="0">No occurrences.</Disclaimer>
          </Div>
        )}
      </div>
    </CollapsibleComponent>
  );
};

const ExpenseRow = ({
  expense,
  onEdit,
  onDelete,
  onOccurrenceChanged,
  withLoader,
  busy,
}: {
  expense: CustomExpenseEntity;
  onEdit: () => void;
  onDelete: () => void;
  /** Fired after an occurrence edit/delete so the parent can refresh. */
  onOccurrenceChanged: () => Promise<void>;
  /** Runs a mutation under the manager's modal-wide overlay spinner. */
  withLoader: (run: () => Promise<void>) => Promise<void>;
  /** True while that overlay is up. */
  busy: boolean;
}) => {
  const amountLabel = formatAmount(expense.amount, NumberType.USD);
  const typeLabel = expense.type === 'oneTime' ? 'One-Time' : 'Recurring';

  const [expanded, setExpanded] = useState(false);
  const [occurrences, setOccurrences] = useState<
    CustomExpenseOccurrenceEntity[] | null
  >(null);
  const [loadingOcc, setLoadingOcc] = useState(false);
  const [editingOcc, setEditingOcc] =
    useState<CustomExpenseOccurrenceEntity | null>(null);

  /** `silent` skips the row spinner. Mutations already show the modal-wide
   *  overlay, and two spinners running back to back read as a stutter. */
  const fetchOccurrences = async (silent = false) => {
    if (!silent) setLoadingOcc(true);
    try {
      const res = await getFinancesCustomExpenseOccurrences(expense.id);
      setOccurrences(res.data.data);
    } catch (error) {
      reportError(error);
    } finally {
      if (!silent) setLoadingOcc(false);
    }
  };

  const toggle = async () => {
    if (loadingOcc) return;
    if (expanded) {
      setExpanded(false);
      return;
    }
    // Lazy-load the list BEFORE expanding so it opens straight to its final
    // height instead of animating open and then resizing when data arrives.
    if (occurrences === null) await fetchOccurrences();
    setExpanded(true);
  };

  const handleDeleteOccurrence = (id: number) =>
    withLoader(async () => {
      try {
        await deleteFinancesCustomExpenseOccurrence(id);
        SuccessToast('Occurrence deleted.');
        await fetchOccurrences(true);
        await onOccurrenceChanged();
      } catch (error) {
        reportError(error);
      }
    });

  // The form is hidden (not unmounted) while `busy` is up, so the overlay
  // behind it is visible during the request and the admin's input is still
  // there if the API rejects the save.
  const handleEditOccurrence = (id: number, amount: number) =>
    withLoader(async () => {
      try {
        await patchFinancesCustomExpenseOccurrence(id, { amount });
        SuccessToast('Month updated.');
        setEditingOcc(null);
        await fetchOccurrences(true);
        // A one-time expense's definition tracks its single month, and
        // past-month edits move the P&L totals, so refresh the list too.
        await onOccurrenceChanged();
      } catch (error) {
        reportError(error);
        // Leave `editingOcc` set: the form returns when the loader clears.
        throw error;
      }
    });

  const viewLink = (
    <Disclaimer
      $m="0"
      $bold
      $pointer
      $underline
      $color={lightTheme.primary}
      onClick={toggle}
      style={{ whiteSpace: 'nowrap' }}
    >
      {expanded ? 'Hide Months' : 'View Months'}
    </Disclaimer>
  );

  return (
    <Div $relative>
      {/* Desktop: table row — gap/padding match TableHeader for alignment */}
      <Div $desktop $borderBottom $borderColor={lightTheme.lightGray}>
        <Div $flex $alignCenter $gap="12px" $p="12px 16px">
          <Div $width={COL.name} $flex $alignCenter $gap="8px">
            {expense.pieColor && (
              <span
                style={{
                  flex: 'none',
                  display: 'inline-block',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: expense.pieColor,
                }}
              />
            )}
            <P $m="0" $bold>
              {expense.name}
            </P>
          </Div>
          <Div $width={COL.amount}>
            <P $m="0">{amountLabel}</P>
            {expense.type === 'recurring' && (
              <Disclaimer $m="0">per month</Disclaimer>
            )}
          </Div>
          <Div $width={COL.type}>
            <P $m="0">{typeLabel}</P>
          </Div>
          <Div $width={COL.period}>
            <P $m="0">{periodLabel(expense)}</P>
          </Div>
          <Div $width={COL.action} $flex $alignCenter $justifyEnd $gap="16px">
            {viewLink}
            <Icon $pointer $size="28" onClick={onEdit}>
              <EditPenIcon
                backgroundColor={lightTheme.primary}
                iconColor={lightTheme.white}
              />
            </Icon>
            <Icon $pointer $size="28" onClick={onDelete}>
              <TrashIconRed />
            </Icon>
          </Div>
        </Div>
        <Div $desktop>
          <OccurrencesCollapsible
            expanded={expanded}
            occurrences={occurrences}
            onEditOccurrence={setEditingOcc}
            onDeleteOccurrence={handleDeleteOccurrence}
          />
        </Div>
      </Div>

      {/* Mobile: stacked card */}
      <Box
        $mobile
        $flex
        $column
        $gap="4px"
        $mb="8"
        $p="12px 16px"
        $pSM="12px 16px"
        style={{ boxShadow: 'none' }}
      >
        <Div $flex $alignStart $between $gap="8px">
          <Div $minWidth="0">
            <H5 $m="0">{expense.name}</H5>
          </Div>
          <Div $flex $alignCenter $gap="16px">
            <Icon $size="28" $pointer onClick={onEdit}>
              <EditPenIcon
                backgroundColor={lightTheme.primary}
                iconColor={lightTheme.white}
              />
            </Icon>
            <Icon $size="28" $pointer onClick={onDelete}>
              <TrashIconRed />
            </Icon>
          </Div>
        </Div>
        <InfoComponentMobile
          title="Amount"
          component={<Disclaimer $m="0">{amountLabel}</Disclaimer>}
        />
        <InfoComponentMobile
          title="Type"
          component={<Disclaimer $m="0">{typeLabel}</Disclaimer>}
        />
        <InfoComponentMobile
          title="Period"
          component={<Disclaimer $m="0">{periodLabel(expense)}</Disclaimer>}
        />

        {/* Centered months toggle at the bottom of the card. */}
        <Div $flex $center $mt="8">
          {viewLink}
        </Div>

        {/* Occurrences live inside the card, below the Period row. */}
        <OccurrencesCollapsible
          expanded={expanded}
          occurrences={occurrences}
          onEditOccurrence={setEditingOcc}
          onDeleteOccurrence={handleDeleteOccurrence}
          marginTop="8px"
        />
      </Box>

      {loadingOcc && <AdminSpinner smallest absolute />}

      <EditOccurrenceModal
        occurrence={editingOcc}
        isOpen={!!editingOcc && !busy}
        monthLabel={
          editingOcc ? occurrenceMonthLabel(editingOcc.periodDate) : ''
        }
        onClose={() => setEditingOcc(null)}
        onSubmit={handleEditOccurrence}
      />
    </Div>
  );
};

/**
 * Manager modal: lists every custom operating expense from the backend (table
 * layout mirrors the VR Order Loss "See Orders" modal), plus a (+) button that
 * opens the AddExpenseModal. Calls `onChanged` after a successful create/edit/
 * delete so the parent can refetch the overview and re-render the pie /
 * headline. Past-month edits move the P&L totals too.
 */
export const ExpensesManagerModal = ({
  isOpen,
  onClose,
  onChanged,
}: {
  isOpen: boolean;
  onClose: () => void;
  onChanged: () => void;
}) => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<CustomExpenseEntity | null>(null);
  const [expenses, setExpenses] = useState<CustomExpenseEntity[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchList = async () => {
    try {
      const res = await getFinancesCustomExpenses();
      setExpenses(res.data.data);
    } catch (error) {
      reportError(error);
    }
  };

  /**
   * The one spinner for every mutation in here. Raised BEFORE the request goes
   * out (otherwise the click sits there with no feedback for a whole round
   * trip) and held until every follow-up refetch has settled, so a save reads
   * as a single load rather than a sequence of them.
   */
  const withLoader = async (run: () => Promise<void>) => {
    setLoading(true);
    try {
      await run();
    } finally {
      setLoading(false);
    }
  };

  // (Re)load the list whenever the manager opens.
  useEffect(() => {
    if (isOpen) withLoader(fetchList);
  }, [isOpen]);

  const refreshAfterMutation = async () => {
    await fetchList();
    onChanged();
  };

  const handleDelete = (id: number) =>
    withLoader(async () => {
      try {
        await deleteFinancesCustomExpense(id);
        SuccessToast('Expense deleted.');
        await refreshAfterMutation();
      } catch (error) {
        reportError(error);
      }
    });

  // The edit form is hidden (not unmounted) while `loading` is up, so the
  // overlay behind it is visible during the request and the admin's input is
  // still there if the API rejects the save.
  const handleEdit = (id: number, body: UpdateCustomExpenseDto) =>
    withLoader(async () => {
      try {
        await patchFinancesCustomExpense(id, body);
        SuccessToast('Expense updated.');
        setEditing(null);
        await refreshAfterMutation();
      } catch (error) {
        reportError(error);
        // Leave `editing` set: the form comes back as soon as the loader clears.
        throw error;
      }
    });

  const handleAdd = (body: CreateCustomExpenseDto) =>
    withLoader(async () => {
      try {
        await postFinancesCustomExpense(body);
        SuccessToast('Expense added.');
        setAdding(false);
        await refreshAfterMutation();
      } catch (error) {
        reportError(error);
        throw error;
      }
    });

  // Both forms step aside while a save is in flight so the overlay below them
  // is what the admin sees. They are hidden, not unmounted, so their inputs
  // survive a rejected save.
  const addFormOpen = adding && !loading;
  const editFormOpen = !!editing && !loading;

  return (
    <>
      <ModalPortal
        // Hide the manager while the add-modal is open; reopen it on close.
        isOpen={isOpen && !addFormOpen}
        closeModal={onClose}
        backgroundColor={lightTheme.white}
        padding="24px"
        logo={false}
        width="800px"
        maxHeight="640px"
        modalOverflow="auto"
        titleWithExit="Custom Expenses"
        // The dashboard's overview loader (AdminSpinner, z-index 200) sits in a
        // non-isolated stacking context, so its z-index leaks to the root and
        // would paint over this modal while `onChanged` refetches. Sit above it
        // (backdrop → 201, content → 202, both clear of the spinner's 200).
        zIndex={201}
      >
        <Div $flex $between $alignCenter $mb="16">
          <Disclaimer $m="0">
            {expenses.length === 0
              ? 'No custom expenses yet.'
              : `${expenses.length} custom ${
                  expenses.length === 1 ? 'expense' : 'expenses'
                }.`}
          </Disclaimer>
          <Button
            $small
            $w="150px"
            $wSM="150px"
            onClick={() => setAdding(true)}
          >
            + Add Expense
          </Button>
        </Div>

        {loading && <AdminSpinner small creme absolute />}

        {expenses.length > 0 ? (
          <>
            <TableHeader columns={COLUMNS} padding="0 16px" gap="12px" />
            {expenses.map((e) => (
              <ExpenseRow
                key={e.id}
                expense={e}
                onEdit={() => setEditing(e)}
                onDelete={() => handleDelete(e.id)}
                // A one-time expense's list amount mirrors its month, so the
                // definition list needs refreshing alongside the overview.
                onOccurrenceChanged={refreshAfterMutation}
                withLoader={withLoader}
                busy={loading}
              />
            ))}
          </>
        ) : (
          <Div $flex $center $height="100px">
            <P $m="0">No custom expenses in this list.</P>
          </Div>
        )}
      </ModalPortal>

      <AddExpenseModal
        isOpen={addFormOpen}
        onClose={() => setAdding(false)}
        onSubmit={handleAdd}
        existingExpenses={expenses}
      />

      <EditExpenseModal
        expense={editing}
        isOpen={editFormOpen}
        onClose={() => setEditing(null)}
        onSubmit={handleEdit}
        existingExpenses={expenses}
      />
    </>
  );
};
